import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, qs } from "@/lib/api";
import { BRANDS } from "@/lib/brands";
import { formatInt, formatWhen } from "@/lib/format";
import { BrandMark } from "@/components/BrandMark";
import { Empty, PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { EventDialog } from "./EventDialogs";
import { LoadError } from "@/components/LoadError";

export interface EventSummary {
  id: number;
  brand_id: number;
  name: string;
  kind: string;
  description: string | null;
  location: string | null;
  time_zone: string;
  status: string;
  demio_event_id: string | null;
  next_session_at: string | null;
  last_session_at: string | null;
  sessions: number;
  registered: number;
  attended: number;
  waitlisted: number;
  guests: number;
}

export const KIND_LABEL: Record<string, string> = { boot_camp: "Boot Camp", webinar: "Webinar", class: "Class", meetup: "Meetup", other: "Event" };

/** Boot Camps, webinars and classes with their next session and how many are registered. */
export function EventsPage() {
  const [when, setWhen] = useState<"upcoming" | "past" | "all">("upcoming");
  const [brand, setBrand] = useState<number | "">("");
  const [kind, setKind] = useState("");
  const [creating, setCreating] = useState(false);
  const q = useQuery({
    queryKey: ["events", when, brand, kind],
    queryFn: () => api<{ events: EventSummary[] }>("/api/v1/events" + qs({ when, brand_id: brand || undefined, kind: kind || undefined, limit: 500 })),
  });
  const rows = q.data?.events ?? [];
  return (
    <div className="mx-auto max-w-[1200px]">
      <PageHeader
        title="Events"
        sub="Boot Camps, webinars and classes"
        actions={
          <>
            <Button size="sm" onClick={() => setCreating(true)}>
              New event
            </Button>
            <div role="radiogroup" aria-label="When" className="flex gap-1">
              {(["upcoming", "past", "all"] as const).map((w) => (
                <Button key={w} size="sm" role="radio" aria-checked={when === w} variant={when === w ? "default" : "outline"} onClick={() => setWhen(w)}>
                  {w === "upcoming" ? "Upcoming" : w === "past" ? "Past" : "All"}
                </Button>
              ))}
            </div>
            <Select className="w-auto" value={brand} onChange={(e) => setBrand(e.target.value ? Number(e.target.value) : "")} aria-label="Brand">
              <option value="">All brands</option>
              {BRANDS.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.short}
                </option>
              ))}
            </Select>
            <Select className="w-auto" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind">
              <option value="">Every kind</option>
              {Object.entries(KIND_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </>
        }
      />
      <Card className="overflow-x-auto">
        {q.isLoading && <Skeleton className="m-3 h-32" />}
        {q.error && <LoadError what="events" error={q.error} onRetry={() => q.refetch()} />}
        {!q.isLoading && !q.error && rows.length === 0 && <Empty title={when === "upcoming" ? "No upcoming events." : "No events here."}>Claude adds events and their dates through the MCP; registrations come from checkout, site forms and the Keap import.</Empty>}
        {rows.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH>Event</TH>
                <TH>Brand</TH>
                <TH>Next date (MT)</TH>
                <TH className="text-right">Registered</TH>
                <TH className="text-right">Guests</TH>
                <TH className="text-right">Waitlist</TH>
                <TH className="text-right">Attended</TH>
                <TH>Status</TH>
              </tr>
            </THead>
            <tbody>
              {rows.map((e) => (
                <TR key={e.id}>
                  <TD className="max-w-[22rem]">
                    <Link to="/events/$eventId" params={{ eventId: String(e.id) }} className="block truncate font-medium text-[var(--color-primary)] hover:underline">
                      {e.name}
                    </Link>
                    <div className="truncate text-xs text-[var(--color-muted-foreground)]">
                      {KIND_LABEL[e.kind] ?? e.kind}
                      {e.location ? `, ${e.location}` : ""}
                      {e.sessions > 1 ? `, ${e.sessions} dates` : ""}
                    </div>
                  </TD>
                  <TD>
                    <BrandMark id={e.brand_id} />
                  </TD>
                  <TD className="whitespace-nowrap">{e.next_session_at ? formatWhen(e.next_session_at) : e.last_session_at ? `last ${formatWhen(e.last_session_at)}` : "no dates yet"}</TD>
                  <TD className="num text-right">{formatInt(e.registered)}</TD>
                  <TD className="num text-right">{e.guests ? formatInt(e.guests) : ""}</TD>
                  <TD className="num text-right">{e.waitlisted ? formatInt(e.waitlisted) : ""}</TD>
                  <TD className="num text-right">{e.attended ? formatInt(e.attended) : ""}</TD>
                  <TD>
                    <Badge variant={e.status === "scheduled" ? "default" : e.status === "cancelled" ? "destructive" : "muted"}>{e.status}</Badge>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      {creating && <EventDialog onClose={() => setCreating(false)} />}
    </div>
  );
}
